
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FileText, CheckSquare, Users, Calendar, Plus, Edit, Download } from "lucide-react";
import NavBar from "@/components/NavBar";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface Meeting {
  id: string;
  title: string;
  date: string;
  attendees: string[];
  status: 'draft' | 'approved' | 'circulated';
}

interface Action {
  id: string;
  meetingId: string;
  description: string;
  assignee: string;
  dueDate: string;
  status: 'open' | 'in-progress' | 'completed' | 'overdue';
  priority: 'low' | 'medium' | 'high';
}

export default function MinutesTracker() {
  const [meetings, setMeetings] = useState<Meeting[]>([
    {
      id: '1',
      title: 'Project Kickoff Meeting',
      date: '2024-01-15',
      attendees: ['John Smith', 'Sarah Johnson', 'Mike Davis'],
      status: 'approved'
    },
    {
      id: '2', 
      title: 'Design Review Meeting',
      date: '2024-01-22',
      attendees: ['Sarah Johnson', 'Mike Davis', 'Emma Wilson'],
      status: 'draft'
    }
  ]);

  const [actions, setActions] = useState<Action[]>([
    {
      id: '1',
      meetingId: '1',
      description: 'Finalize project scope document',
      assignee: 'John Smith',
      dueDate: '2024-02-01',
      status: 'in-progress',
      priority: 'high'
    },
    {
      id: '2',
      meetingId: '1', 
      description: 'Set up project repository',
      assignee: 'Mike Davis',
      dueDate: '2024-01-25',
      status: 'completed',
      priority: 'medium'
    },
    {
      id: '3',
      meetingId: '2',
      description: 'Review design mockups',
      assignee: 'Emma Wilson',
      dueDate: '2024-01-30',
      status: 'overdue',
      priority: 'high'
    }
  ]);

  const [newMeeting, setNewMeeting] = useState({
    title: '',
    date: '',
    attendees: ''
  });

  const [newAction, setNewAction] = useState({
    meetingId: '',
    description: '',
    assignee: '',
    dueDate: '',
    priority: 'medium' as Action['priority']
  });

  const addMeeting = () => {
    if (newMeeting.title && newMeeting.date) {
      const meeting: Meeting = {
        id: Date.now().toString(),
        title: newMeeting.title,
        date: newMeeting.date,
        attendees: newMeeting.attendees.split(',').map(a => a.trim()),
        status: 'draft'
      };
      setMeetings([...meetings, meeting]);
      setNewMeeting({ title: '', date: '', attendees: '' });
    }
  };

  const addAction = () => {
    if (newAction.description && newAction.assignee && newAction.dueDate && newAction.meetingId) {
      const action: Action = {
        id: Date.now().toString(),
        meetingId: newAction.meetingId,
        description: newAction.description,
        assignee: newAction.assignee,
        dueDate: newAction.dueDate,
        status: 'open',
        priority: newAction.priority
      };
      setActions([...actions, action]);
      setNewAction({ meetingId: '', description: '', assignee: '', dueDate: '', priority: 'medium' });
    }
  };

  const getStatusColor = (status: Action['status']) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in-progress': return 'bg-blue-100 text-blue-800';
      case 'overdue': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: Action['priority']) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <NavBar />
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
            <FileText className="h-8 w-8 text-primary" />
            Minutes & Action Tracker
          </h1>
          <p className="text-muted-foreground">
            Manage meeting minutes and track action items with comprehensive workflow tools
          </p>
        </div>

        <Tabs defaultValue="meetings" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="meetings">Meetings</TabsTrigger>
            <TabsTrigger value="actions">Action Items</TabsTrigger>
            <TabsTrigger value="templates">Templates</TabsTrigger>
            <TabsTrigger value="reports">Reports</TabsTrigger>
          </TabsList>

          <TabsContent value="meetings" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Meeting Minutes
                  </span>
                  <Button onClick={addMeeting}>
                    <Plus className="h-4 w-4 mr-2" />
                    New Meeting
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Add New Meeting</h3>
                    <div className="space-y-2">
                      <Label htmlFor="meetingTitle">Meeting Title</Label>
                      <Input
                        id="meetingTitle"
                        placeholder="Enter meeting title"
                        value={newMeeting.title}
                        onChange={(e) => setNewMeeting({...newMeeting, title: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="meetingDate">Date</Label>
                      <Input
                        id="meetingDate"
                        type="date"
                        value={newMeeting.date}
                        onChange={(e) => setNewMeeting({...newMeeting, date: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="attendees">Attendees (comma-separated)</Label>
                      <Input
                        id="attendees"
                        placeholder="John Smith, Sarah Johnson, Mike Davis"
                        value={newMeeting.attendees}
                        onChange={(e) => setNewMeeting({...newMeeting, attendees: e.target.value})}
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Recent Meetings</h3>
                  <div className="space-y-4">
                    {meetings.map((meeting) => (
                      <Card key={meeting.id}>
                        <CardContent className="pt-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-semibold">{meeting.title}</h4>
                              <p className="text-sm text-muted-foreground flex items-center gap-2 mt-1">
                                <Calendar className="h-4 w-4" />
                                {new Date(meeting.date).toLocaleDateString()}
                              </p>
                              <p className="text-sm text-muted-foreground mt-1">
                                Attendees: {meeting.attendees.join(', ')}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant={meeting.status === 'approved' ? 'default' : 'secondary'}>
                                {meeting.status}
                              </Badge>
                              <Button variant="ghost" size="sm">
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm">
                                <Download className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="actions" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <CheckSquare className="h-5 w-5" />
                    Action Items
                  </span>
                  <Button onClick={addAction}>
                    <Plus className="h-4 w-4 mr-2" />
                    New Action
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Add New Action Item</h3>
                    <div className="space-y-2">
                      <Label htmlFor="meetingSelect">Meeting</Label>
                      <Select value={newAction.meetingId} onValueChange={(value) => setNewAction({...newAction, meetingId: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select meeting" />
                        </SelectTrigger>
                        <SelectContent>
                          {meetings.map((meeting) => (
                            <SelectItem key={meeting.id} value={meeting.id}>
                              {meeting.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="actionDescription">Description</Label>
                      <Textarea
                        id="actionDescription"
                        placeholder="Describe the action item"
                        value={newAction.description}
                        onChange={(e) => setNewAction({...newAction, description: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="assignee">Assignee</Label>
                      <Input
                        id="assignee"
                        placeholder="Enter assignee name"
                        value={newAction.assignee}
                        onChange={(e) => setNewAction({...newAction, assignee: e.target.value})}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="dueDate">Due Date</Label>
                        <Input
                          id="dueDate"
                          type="date"
                          value={newAction.dueDate}
                          onChange={(e) => setNewAction({...newAction, dueDate: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="priority">Priority</Label>
                        <Select value={newAction.priority} onValueChange={(value: Action['priority']) => setNewAction({...newAction, priority: value})}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="low">Low</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Action Items</h3>
                  <div className="space-y-4">
                    {actions.map((action) => {
                      const meeting = meetings.find(m => m.id === action.meetingId);
                      return (
                        <Card key={action.id}>
                          <CardContent className="pt-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <p className="font-medium">{action.description}</p>
                                <p className="text-sm text-muted-foreground mt-1">
                                  Meeting: {meeting?.title || 'Unknown'}
                                </p>
                                <p className="text-sm text-muted-foreground">
                                  Assigned to: {action.assignee}
                                </p>
                                <p className="text-sm text-muted-foreground">
                                  Due: {new Date(action.dueDate).toLocaleDateString()}
                                </p>
                              </div>
                              <div className="flex flex-col gap-2">
                                <Badge className={getStatusColor(action.status)}>
                                  {action.status}
                                </Badge>
                                <Badge className={getPriorityColor(action.priority)}>
                                  {action.priority}
                                </Badge>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="templates" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Meeting Templates</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card className="cursor-pointer hover:shadow-md transition-shadow">
                    <CardContent className="pt-6">
                      <h3 className="font-semibold mb-2">Project Kickoff Template</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Standard template for project initiation meetings
                      </p>
                      <Button variant="outline" className="w-full">Use Template</Button>
                    </CardContent>
                  </Card>
                  
                  <Card className="cursor-pointer hover:shadow-md transition-shadow">
                    <CardContent className="pt-6">
                      <h3 className="font-semibold mb-2">Design Review Template</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Template for design review and approval meetings
                      </p>
                      <Button variant="outline" className="w-full">Use Template</Button>
                    </CardContent>
                  </Card>
                  
                  <Card className="cursor-pointer hover:shadow-md transition-shadow">
                    <CardContent className="pt-6">
                      <h3 className="font-semibold mb-2">Progress Review Template</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Template for regular progress review meetings
                      </p>
                      <Button variant="outline" className="w-full">Use Template</Button>
                    </CardContent>
                  </Card>
                  
                  <Card className="cursor-pointer hover:shadow-md transition-shadow">
                    <CardContent className="pt-6">
                      <h3 className="font-semibold mb-2">Stakeholder Update Template</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Template for stakeholder communication meetings
                      </p>
                      <Button variant="outline" className="w-full">Use Template</Button>
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="reports" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Reports & Analytics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <div className="text-2xl font-bold text-primary">{meetings.length}</div>
                      <p className="text-sm text-muted-foreground">Total Meetings</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <div className="text-2xl font-bold text-green-600">{actions.filter(a => a.status === 'completed').length}</div>
                      <p className="text-sm text-muted-foreground">Completed Actions</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <div className="text-2xl font-bold text-red-600">{actions.filter(a => a.status === 'overdue').length}</div>
                      <p className="text-sm text-muted-foreground">Overdue Actions</p>
                    </CardContent>
                  </Card>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>Meeting Minutes Report</div>
                    </div>
                  </Button>
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>Action Items Summary</div>
                    </div>
                  </Button>
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>Attendance Report</div>
                    </div>
                  </Button>
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>Performance Analytics</div>
                    </div>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
